<template>
  <section id="work" class="section projects" aria-labelledby="work-title">
    <div class="container">
      <header class="section-heading">
        <p class="section-heading__index mono">01 / Selected work</p>
        <div class="section-heading__copy">
          <h2 id="work-title">A world, built as a system.</h2>
          <p>
            Ulna brings together engine work, multiplayer architecture,
            scripting, content tooling, and a public web product.
          </p>
        </div>
      </header>

      <article class="project">
        <a
          class="project__hero"
          href="https://playulna.com/"
          target="_blank"
          rel="noopener"
          aria-label="Explore Ulna at playulna.com"
        >
          <img
            src="/ulna-five-roads.webp"
            width="1600"
            height="900"
            loading="lazy"
            alt="Five Ulna vocations crossing Stonebrook’s rain gate toward five roads"
          />
          <span class="project__hero-shade" aria-hidden="true"></span>
          <span class="project__wordmark" aria-hidden="true">ULNA</span>
          <span class="project__hero-caption">
            <span class="mono">World direction · Five roads, one realm</span>
            <strong>
              Explore playulna.com
              <span aria-hidden="true">↗</span>
            </strong>
          </span>
        </a>

        <div class="project__body">
          <div class="project__story">
            <p class="project__eyebrow mono">
              Flagship project · Active development
            </p>
            <h3>Ulna — a 2D isometric MMORPG</h3>
            <p>
              A dark, story-driven pixel-art world with hand-authored regions
              and an authoritative multiplayer architecture. The engine,
              server, content runtime, tooling, and companion site are designed
              as one system.
            </p>
            <a
              class="project__link"
              href="https://playulna.com/"
              target="_blank"
              rel="noopener"
            >
              Visit the Ulna site at playulna.com
              <span aria-hidden="true">↗</span>
            </a>
          </div>

          <dl class="project__facts">
            <div>
              <dt class="mono">01 / Engine &amp; server</dt>
              <dd>
                Rust with Bevy ECS, including the authoritative multiplayer
                server.
              </dd>
            </div>
            <div>
              <dt class="mono">02 / Content runtime</dt>
              <dd>Lua scripting for content authoring and modding.</dd>
            </div>
            <div>
              <dt class="mono">03 / Asset pipeline</dt>
              <dd>
                Python tooling for deterministic world generation, validation,
                and content audits.
              </dd>
            </div>
            <div>
              <dt class="mono">04 / Companion site</dt>
              <dd>Next.js, deployed on Vercel at playulna.com.</dd>
            </div>
          </dl>
        </div>

        <div class="project__evidence">
          <figure class="project__capture project__capture--wide">
            <div class="project__image-frame">
              <img
                src="../assets/ulna/ashen-gate.webp"
                width="1280"
                height="800"
                loading="lazy"
                alt="Ulna gameplay at the Ashen Catacomb return gate, with the multiplayer game interface visible"
              />
            </div>
            <figcaption>
              <span class="mono">Live game capture</span>
              <strong>Authoritative world state, game UI, and content runtime</strong>
            </figcaption>
          </figure>

          <figure class="project__capture">
            <div class="project__image-frame">
              <img
                src="../assets/ulna/near-rift-depths.webp"
                width="980"
                height="600"
                loading="lazy"
                alt="Ulna player exploring by torchlight in the Ashen Seal Near Rift"
              />
            </div>
            <figcaption>
              <span class="mono">World capture</span>
              <strong>Hand-authored region design in the Near Rift</strong>
            </figcaption>
          </figure>
        </div>

        <ul class="project__stack" aria-label="Ulna technology stack">
          <li>Rust</li>
          <li>Bevy ECS</li>
          <li>Lua</li>
          <li>Python</li>
          <li>Next.js</li>
          <li>Vercel</li>
        </ul>
      </article>
    </div>
  </section>
</template>

<script>
export default { name: "Projects" };
</script>

<style lang="scss" scoped>
.projects {
  position: relative;
}

.project {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  background: var(--surface);
  box-shadow: var(--shadow);
}

.project::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: inherit;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  pointer-events: none;
}

.project__hero {
  position: relative;
  display: block;
  overflow: hidden;
  aspect-ratio: 16 / 7.4;
  border-bottom: 1px solid var(--border);
  background: #06090d;
  color: var(--text);
}

.project__hero img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 44%;
  filter: saturate(0.72) contrast(1.08) brightness(0.82);
  transition: filter 700ms ease, transform 900ms cubic-bezier(0.22, 1, 0.36, 1);
}

.project__hero:hover {
  text-decoration: none;
}

.project__hero:hover img {
  filter: saturate(0.9) contrast(1.06) brightness(0.92);
  transform: scale(1.018);
}

.project__hero-shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(5, 9, 13, 0.6), transparent 44%),
    linear-gradient(0deg, rgba(5, 9, 13, 0.86), transparent 52%);
}

.project__wordmark {
  position: absolute;
  left: clamp(22px, 4vw, 52px);
  bottom: clamp(62px, 8vw, 92px);
  color: rgba(246, 248, 247, 0.96);
  font-family: Georgia, "Times New Roman", serif;
  font-size: clamp(54px, 10vw, 118px);
  font-weight: 600;
  letter-spacing: 0.2em;
  line-height: 0.8;
  text-shadow: 0 8px 36px rgba(0, 0, 0, 0.7);
}

.project__wordmark::after {
  content: "";
  display: block;
  width: 42%;
  height: 1px;
  margin: 16px 0 0 3px;
  background: linear-gradient(90deg, var(--accent), transparent);
}

.project__hero-caption {
  position: absolute;
  right: clamp(22px, 4vw, 52px);
  bottom: clamp(24px, 3.5vw, 42px);
  display: grid;
  gap: 6px;
  color: var(--text);
  text-align: right;
}

.project__hero-caption > span {
  color: rgba(238, 244, 243, 0.68);
  font-size: 9px;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.project__hero-caption strong {
  font-size: 13px;
  font-weight: 680;
}

.project__hero-caption strong span {
  margin-left: 5px;
  color: var(--accent);
}

.project__body {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(360px, 0.95fr);
  gap: clamp(42px, 7vw, 86px);
  padding: clamp(36px, 6vw, 72px);
}

.project__eyebrow {
  margin: 0 0 var(--space-4);
  color: var(--accent);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.project__story h3 {
  max-width: 14ch;
  font-size: clamp(32px, 4.3vw, 52px);
  font-weight: 700;
  letter-spacing: -0.05em;
  line-height: 1.02;
}

.project__story > p:not(.project__eyebrow) {
  max-width: 59ch;
  margin: var(--space-5) 0 0;
  color: var(--muted);
  font-size: 16px;
  line-height: 1.75;
}

.project__link {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: var(--space-6);
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(124, 226, 201, 0.42);
  color: var(--text);
  font-size: 13px;
  font-weight: 680;
}

.project__link:hover {
  border-color: var(--accent);
  color: var(--accent-strong);
  text-decoration: none;
}

.project__facts {
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--border);
}

.project__facts > div {
  display: grid;
  grid-template-columns: 144px minmax(0, 1fr);
  gap: var(--space-4);
  padding: 17px 0;
  border-bottom: 1px solid var(--border);
}

.project__facts dt {
  color: var(--accent-2);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.project__facts dd {
  margin: 0;
  color: var(--text-soft);
  font-size: 12px;
  line-height: 1.55;
}

.project__evidence {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(260px, 0.75fr);
  gap: var(--space-4);
  padding: 0 clamp(18px, 3vw, 36px) clamp(18px, 3vw, 36px);
}

.project__capture {
  display: flex;
  min-width: 0;
  margin: 0;
  flex-direction: column;
}

.project__image-frame {
  position: relative;
  flex: 1 1 auto;
  overflow: hidden;
  min-height: 300px;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: #06090d;
}

.project__image-frame::after {
  content: "";
  position: absolute;
  inset: 0;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.025);
  pointer-events: none;
}

.project__capture img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(0.82) contrast(1.05);
  transition: filter 500ms ease, transform 700ms cubic-bezier(0.22, 1, 0.36, 1);
}

.project__capture:hover img {
  filter: saturate(1) contrast(1.04);
  transform: scale(1.015);
}

.project__capture--wide img {
  object-position: center;
}

.project__capture figcaption {
  display: grid;
  gap: 3px;
  padding: 12px 3px 0;
}

.project__capture figcaption span {
  color: var(--accent);
  font-size: 8px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.project__capture figcaption strong {
  color: var(--muted);
  font-size: 11px;
  font-weight: 560;
}

.project__stack {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--border);
  background: rgba(6, 10, 14, 0.42);
  list-style: none;
}

.project__stack li {
  padding: 15px 10px;
  border-right: 1px solid var(--border);
  color: var(--muted);
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 9px;
  font-weight: 650;
  letter-spacing: 0.09em;
  text-align: center;
  text-transform: uppercase;
}

.project__stack li:last-child {
  border-right: 0;
}

@media (max-width: 860px) {
  .project__hero {
    aspect-ratio: 16 / 9;
  }

  .project__body {
    grid-template-columns: 1fr;
    gap: 42px;
  }

  .project__evidence {
    grid-template-columns: 1fr;
  }

  .project__image-frame {
    min-height: 0;
    aspect-ratio: 8 / 5;
  }

  .project__stack {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .project__stack li:nth-child(3) {
    border-right: 0;
  }

  .project__stack li:nth-child(n + 4) {
    border-top: 1px solid var(--border);
  }
}

@media (max-width: 600px) {
  .project {
    border-radius: var(--radius-lg);
  }

  .project__hero {
    aspect-ratio: 4 / 3;
  }

  .project__hero img {
    object-position: 53% center;
  }

  .project__wordmark {
    bottom: 76px;
    font-size: clamp(42px, 16vw, 64px);
  }

  .project__hero-caption {
    right: 20px;
    bottom: 19px;
  }

  .project__hero-caption > span {
    display: none;
  }

  .project__body {
    padding: 34px 22px 40px;
  }

  .project__facts > div {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .project__evidence {
    padding: 0 12px 16px;
  }

  .project__capture figcaption {
    padding-right: 8px;
    padding-left: 8px;
  }
}
</style>
